import { forwardRef, useId } from "react";
import { cx } from "@/utils/cx";
import type { InputHTMLAttributes, ReactNode } from "react";

/**
 * Gymkee 2027 text field (spec text-input 1.3; sheet DS05).
 *
 * The label sits above at 13 secondary and stays there; a placeholder is never the label. The
 * well is the `.ds-field` frame, which this app takes at 52 through `.ds-pay-form` in
 * src/design/pay.css (checkout 1.4's form region asks for 52 on a money page). Validation runs
 * on blur and again on submit, never on every keystroke; the error replaces the helper as one
 * sentence in the danger text colour, linked to the input by `aria-describedby` so a screen
 * reader reads it with the field.
 */

export interface FieldProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "size"
> {
  label: string;
  /** One sentence saying what happened and what to do; it replaces the helper. */
  error?: string | null;
  helper?: string;
  /** A control at the trailing edge of the well (the coupon's Apply). */
  affix?: ReactNode;
  className?: string;
}

export const Field = forwardRef<HTMLInputElement, FieldProps>(
  (
    { label, error, helper, affix, className, id: givenId, disabled, ...props },
    ref,
  ) => {
    const generated = useId();
    const id = givenId ?? `field-${generated}`;
    const messageId = `${id}-message`;
    const message = error ?? helper ?? null;

    return (
      <div className={cx("gap-gk-2 flex flex-col", className)}>
        <label htmlFor={id} className="ds-label">
          {label}
        </label>
        <div
          className={cx("ds-field", {
            "ds-field-error": !!error,
            "ds-field-disabled": !!disabled,
          })}
          aria-disabled={disabled || undefined}
        >
          <input
            ref={ref}
            id={id}
            className="ds-field-input"
            disabled={disabled}
            aria-invalid={error ? true : undefined}
            aria-describedby={message ? messageId : undefined}
            {...props}
          />
          {affix}
        </div>
        {message && (
          <p
            id={messageId}
            className={error ? "ds-error-message" : "ds-helper"}
          >
            {message}
          </p>
        )}
      </div>
    );
  },
);

Field.displayName = "Field";
